import axios from "axios";
import { toast } from "react-toastify";
import { store } from "../index";
import { getAllTeamDetailsByPatient } from "./ConsultsService";

const getCallerContext = () => {
  const state = store.getState();
  return {
    id: state?.providerDetailsReducer?.provider?.id,
    token: state?.providerDetailsReducer?.token,
  };
};

export const getOnlineSpecialists = async ({ patientId, specialtyId }) => {
  try {
    const { id, token } = getCallerContext();
    const { data } = await axios.post(
      "api/providerEndpoints/v1/getOnlineSpecialists",
      {
        patientId: Number(patientId),
        specialtyId: Number(specialtyId),
        id,
        token,
      }
    );

    if (!data?.status) {
      toast.error(data?.errorMessage || "Unable to fetch online specialists");
      return {
        status: false,
        specialists: [],
        onlineCount: 0,
        totalCount: 0,
      };
    }

    return {
      status: true,
      specialists: data.specialists || [],
      onlineCount: data.onlineCount ?? 0,
      totalCount: data.totalCount ?? 0,
    };
  } catch (error) {
    console.error("getOnlineSpecialists Error:", error);
    toast.error("Something went wrong fetching online specialists");
    return { status: false, specialists: [], onlineCount: 0, totalCount: 0 };
  }
};

export const requestSpecialtyConsult = async ({
  patientId,
  specialtyId,
  urgent,
  clinicalQuestion,
  targetProviderId,
}) => {
  try {
    const { id, token } = getCallerContext();
    const { data } = await axios.post(
      "api/patientEndpoints/v1/requestSpecialtyConsult",
      {
        patientId: Number(patientId),
        specialtyId: Number(specialtyId),
        urgent: !!urgent,
        clinicalQuestion,
        ...(targetProviderId && {
          targetProviderId: Number(targetProviderId),
        }),
        id,
        token,
      }
    );

    if (!data?.status) {
      toast.error(data?.errorMessage || "Unable to request specialty consult");
      return { status: false };
    }

    return data;
  } catch (error) {
    console.error("requestSpecialtyConsult Error:", error);
    toast.error("Something went wrong requesting the specialty consult");
    return { status: false };
  }
};

export const acceptSpecialtyConsult = async ({ patientId, consultId }) => {
  try {
    const { id, token } = getCallerContext();
    const { data } = await axios.post(
      "api/patientEndpoints/v1/acceptSpecialtyConsult",
      {
        patientId: Number(patientId),
        consultId: Number(consultId),
        id,
        token,
      }
    );

    if (!data?.status) {
      toast.error(data?.errorMessage || "Unable to accept specialty consult");
      return { status: false };
    }

    return data;
  } catch (error) {
    console.error("acceptSpecialtyConsult Error:", error);
    toast.error("Something went wrong accepting the specialty consult");
    return { status: false };
  }
};

export const declineSpecialtyConsult = async ({ patientId, consultId }) => {
  try {
    const { id, token } = getCallerContext();
    const { data } = await axios.post(
      "api/patientEndpoints/v1/declineSpecialtyConsult",
      {
        patientId: Number(patientId),
        consultId: Number(consultId),
        id,
        token,
      }
    );

    if (!data?.status) {
      toast.error(data?.errorMessage || "Unable to decline specialty consult");
      return { status: false };
    }

    return data;
  } catch (error) {
    console.error("declineSpecialtyConsult Error:", error);
    toast.error("Something went wrong declining the specialty consult");
    return { status: false };
  }
};

export const completeSpecialtyConsult = async ({
  patientId,
  consultId,
  outcome,
  summary,
}) => {
  try {
    const { id, token } = getCallerContext();
    const { data } = await axios.post(
      "api/patientEndpoints/v1/completeSpecialtyConsult",
      {
        patientId: Number(patientId),
        consultId: Number(consultId),
        outcome,
        summary,
        id,
        token,
      }
    );

    if (!data?.status) {
      toast.error(data?.errorMessage || "Unable to complete specialty consult");
      return { status: false };
    }

    return data;
  } catch (error) {
    console.error("completeSpecialtyConsult Error:", error);
    toast.error("Something went wrong completing the specialty consult");
    return { status: false };
  }
};

export const cancelSpecialtyConsult = async ({ patientId, consultId }) => {
  try {
    const { id, token } = getCallerContext();
    const { data } = await axios.post(
      "api/patientEndpoints/v1/cancelSpecialtyConsult",
      {
        patientId: Number(patientId),
        consultId: Number(consultId),
        id,
        token,
      }
    );

    if (!data?.status) {
      toast.error(data?.errorMessage || "Unable to cancel specialty consult");
      return { status: false };
    }

    return data;
  } catch (error) {
    console.error("cancelSpecialtyConsult Error:", error);
    toast.error("Something went wrong cancelling the specialty consult");
    return { status: false };
  }
};

/**
 * Parallel consults: route an accept to the right endpoint.
 *
 * A SPECIALTY consult shows up in the remote clinician's pending list like any
 * other, but legacy acceptInvite rejects it — that endpoint guards on the
 * PATIENT's status, and the patient is already Active under the primary consult,
 * so it fails with "consultation request already accepted by someone". The
 * mobile clients route specialty accepts explicitly; the portal's lists did not.
 *
 * Resolves the consult for this patient and dispatches accordingly. Falls back
 * to the legacy path whenever there is no pending specialty consult.
 */
export const acceptConsultRouted = async ({
  patientId,
  consultId,
  legacyAccept,
}) => {
  try {
    const details = await getAllTeamDetailsByPatient(patientId);
    const consults = [
      ...(details?.currentTeams || []),
      ...(details?.teamHistory || []),
    ];
    const specialty = consults.find(
      (c) =>
        String(c?.consultType).toUpperCase() === "SPECIALTY" &&
        c?.consultStatus === "Pending" &&
        (consultId == null || Number(c.consultId) === Number(consultId))
    );
    if (specialty) {
      return acceptSpecialtyConsult({
        patientId,
        consultId: specialty.consultId,
      });
    }
  } catch (error) {
    console.error("acceptConsultRouted: falling back to legacy accept", error);
  }
  return legacyAccept();
};

/**
 * Specialties this patient's hospital can actually be consulted for — derived
 * from the clinicians who work it, not from the global catalog. Falls back to
 * the catalog if the backend predates the endpoint.
 */
export const getAvailableSpecialties = async ({ patientId }) => {
  try {
    const { id, token } = getCallerContext();
    const { data } = await axios.post(
      "api/providerEndpoints/v1/getAvailableSpecialties",
      { patientId: Number(patientId), id, token }
    );
    if (!data?.status) return { status: false, specialties: [] };
    return {
      status: true,
      specialties: (data.specialists || []).map((s) => ({
        id: s.providerId,
        name: s.name,
        onlineCount: s.totalPatientCount ?? 0,
        totalCount: s.maxPatientCount ?? 0,
      })),
    };
  } catch (error) {
    console.error("getAvailableSpecialties Error:", error);
    return { status: false, specialties: [] };
  }
};
